/**
 * MicButton.jsx — Deepgram-powered speech-to-text microphone button
 *
 * Usage: <MicButton onTranscript={(text) => setInput(prev => prev + ' ' + text)} />
 *
 * Records audio via MediaRecorder, sends to /api/transcribe,
 * returns the transcript to the parent via onTranscript callback.
 */
import { useState, useRef } from 'react';

export default function MicButton({ onTranscript, disabled, size = 18 }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (chunks.current.length === 0) return;

        setProcessing(true);
        try {
          const blob = new Blob(chunks.current, { type: 'audio/webm' });
          const form = new FormData();
          form.append('audio', blob, 'recording.webm');

          const res = await fetch('/api/transcribe', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + localStorage.getItem('mickey_token') },
            body: form,
          });

          if (res.ok) {
            const data = await res.json();
            if (data.transcript) onTranscript(data.transcript);
          }
        } catch (err) {
          console.error('Transcription failed:', err);
        }
        setProcessing(false);
      };

      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      console.error('Mic access denied:', err);
    }
  }

  function stopRecording() {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  }

  function toggle() {
    if (recording) stopRecording();
    else startRecording();
  }

  if (processing) {
    return (
      <button
        type="button"
        disabled
        className="shrink-0 p-1.5 rounded-full bg-gray-100 dark:bg-slate-700"
        title="Transcribing..."
      >
        <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin text-blue-500">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
        </svg>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={
        "shrink-0 p-1.5 rounded-full transition-colors " +
        (recording
          ? "bg-red-500 text-white animate-pulse"
          : "bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600")
      }
      title={recording ? "Stop recording" : "Speak your answer"}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
    </button>
  );
}
